import { Button, Input, Label } from '@devizy/ui';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { useTranslations } from 'use-intl';
import { z } from 'zod';

import { ChatAuthPanel } from '@/components/chat/chat-auth-panel';
import { ChatRoomClient } from '@/components/chat/chat-room';
import { ChatShell } from '@/components/chat/chat-shell';
import { signOutChatGuest } from '@/lib/chat-auth';
import { publicEnv } from '@/lib/env';
import { trpc } from '@/lib/trpc/client';

const searchSchema = z.object({
  invitation_id: z.string().optional(),
});

export const Route = createFileRoute('/$projectId')({
  validateSearch: searchSchema,
  component: ProjectChatPage,
});

function ProjectChatPage(): React.ReactElement {
  const { projectId } = Route.useParams();
  const search = Route.useSearch();
  const t = useTranslations('chat');
  const utils = trpc.useUtils();
  const [displayName, setDisplayName] = React.useState('');
  const [invitationDismissed, setInvitationDismissed] = React.useState(false);

  const roomQuery = trpc.chat.roomByProject.useQuery({ projectId }, { retry: false });
  const roomId = roomQuery.data?.roomId;

  const identityQuery = trpc.chat.guestIdentity.useQuery(
    { roomId: roomId ?? '' },
    { enabled: Boolean(roomId) },
  );
  const activate = trpc.chat.activateGuest.useMutation({
    onSuccess: () => {
      void utils.chat.guestIdentity.invalidate();
      void utils.chat.roomByProject.invalidate({ projectId });
    },
  });

  const identity = identityQuery.data;
  const isGuest = identity?.isGuest === true;
  const needsActivation = isGuest && identity.activated === false;

  const room = roomQuery.data;
  const viewer = room?.participants.find((p) => p.id === room.viewerParticipantId);
  const viewerLabel = viewer?.displayName ?? viewer?.guestEmail ?? null;
  const invitationIgnored =
    search.invitation_id !== undefined && room !== undefined && room.viewerKind === 'USER';

  const leave = (): void => {
    void signOutChatGuest().finally(() => {
      window.location.href = publicEnv.appUrl;
    });
  };

  const loading = <p className="text-muted-foreground text-sm">{t('loading')}</p>;

  if (roomQuery.isPending) {
    return <ChatShell centered>{loading}</ChatShell>;
  }

  if (roomQuery.isError) {
    return (
      <ChatShell centered>
        <ChatAuthPanel
          projectId={projectId}
          invitationId={search.invitation_id ?? null}
          onAuthenticated={(): void => {
            void utils.chat.roomByProject.invalidate({ projectId });
          }}
        />
      </ChatShell>
    );
  }

  if (identityQuery.isPending) {
    return <ChatShell centered>{loading}</ChatShell>;
  }

  if (needsActivation) {
    return (
      <ChatShell context={t('guest.accessBadge')} centered>
        <section
          className="border-border bg-background w-full max-w-md rounded-xl border p-6 shadow-sm"
          data-testid="guest-activation"
        >
          <h1 className="font-display text-lg tracking-tight">{t('guest.activateTitle')}</h1>
          <p className="text-muted-foreground mt-1.5 text-sm">{t('guest.activateBody')}</p>
          {identity?.email ? (
            <p className="text-muted-foreground mt-3 text-xs">
              {t('guest.activateInvitedAs', { email: identity.email })}
            </p>
          ) : null}
          <div className="mt-5 space-y-2">
            <Label htmlFor="guest-display-name">{t('guest.activateNameLabel')}</Label>
            <Input
              id="guest-display-name"
              value={displayName}
              maxLength={80}
              onChange={(e): void => setDisplayName(e.target.value)}
              placeholder={t('guest.activateNamePlaceholder')}
            />
          </div>
          <Button
            className="mt-4 w-full"
            data-testid="guest-activate-submit"
            disabled={activate.isPending || displayName.trim().length === 0 || !roomId}
            onClick={(): void => {
              if (!roomId) return;
              activate.mutate({ roomId, displayName: displayName.trim() });
            }}
          >
            {activate.isPending ? t('compose.sending') : t('guest.activateSubmit')}
          </Button>
        </section>
      </ChatShell>
    );
  }

  if (invitationIgnored && !invitationDismissed) {
    return (
      <ChatShell centered>
        <section
          className="border-border bg-background w-full max-w-md rounded-xl border p-6 shadow-sm"
          data-testid="chat-identity-conflict"
        >
          <h1 className="font-display text-lg tracking-tight">{t('auth.alreadyMemberTitle')}</h1>
          <p className="text-muted-foreground mt-1.5 text-sm">
            {t('auth.alreadyMemberBody', { name: viewerLabel ?? t('auth.thisAccount') })}
          </p>
          <p className="text-muted-foreground mt-3 text-xs">{t('auth.alreadyMemberHint')}</p>
          <Button
            className="mt-5 w-full"
            data-testid="chat-identity-continue"
            onClick={(): void => setInvitationDismissed(true)}
          >
            {t('auth.alreadyMemberCta', { name: viewerLabel ?? t('auth.thisAccount') })}
          </Button>
        </section>
      </ChatShell>
    );
  }

  return (
    <ChatShell
      context={viewerLabel ? t('auth.signedInAs', { name: viewerLabel }) : null}
      actions={
        isGuest ? (
          <Button variant="outline" size="sm" onClick={leave} data-testid="guest-signout">
            {t('auth.signOut')}
          </Button>
        ) : (
          <Button asChild variant="outline" size="sm">
            <a href={publicEnv.appUrl}>{t('auth.memberCta')}</a>
          </Button>
        )
      }
    >
      <ChatRoomClient projectId={projectId} isGuest={isGuest} />
    </ChatShell>
  );
}
